import { Auth, getAuth, signInWithCustomToken, signOut } from 'firebase/auth';
import { FirebaseApp, getApp, getApps, initializeApp } from 'firebase/app';
import {
  Firestore,
  QuerySnapshot,
  Unsubscribe,
  collection,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
  where
} from 'firebase/firestore';
import {
  RemoteConfig,
  fetchAndActivate,
  getRemoteConfig,
  getValue,
} from 'firebase/remote-config';
import { 
  getAnalytics, 
  logEvent as webLogEvent, 
  setUserId as webSetUserId,
  setUserProperties as webSetUserProperties 
} from 'firebase/analytics';


import { fetchFirebaseCustomToken } from '@lib/api';
import { logger } from '@lib/logger';
import { setData } from '@lib/storage';

import { config as firebaseConfig } from './config/config'; // eslint-disable-line import/no-unresolved
import { makeOnMultipleQueriesSnapshot } from './onMultipleQueriesSnapshot';
import { Firebase, OnQuerySnapshotInput, WhereClause } from './types';

// 1. Safe Initialization (Babel/HMR Friendly)
const app: FirebaseApp = !getApps().length ? initializeApp(firebaseConfig) : getApp();

// 2. Lazy Getters (Ensures components are registered before access)
const getFirebaseAuth = (): Auth => getAuth(app);
const getFirebaseDb = (): Firestore => getFirestore(app);
const getFirebaseRC = (): RemoteConfig => {
  const rc = getRemoteConfig(app);
  // Configure only once
  rc.settings.minimumFetchIntervalMillis = 1000; 
  rc.defaultConfig = {
    HANDLING_TIME: 5,
    LOG_LAST_SEEN: 5,
    BULK_ITEMS_AVAILABILITY: false,
    HIDE_TILL_TURN_BACK_OPTION: false,
    HIDE_FOOD_IS_READY_BUTTON: false,
    SUPPORT_NUMBER: '01127802226',
    APPLY_FORCE_UPDATE: true,
  };
  return rc;
};

export function fetchAndActivateRemoteConfig() {
  return fetchAndActivate(getFirebaseRC())
    .then((activate) => {
      // ...
      activate
        ? console.log('the fetched call was activated.')
        : console.log('we’re using the already fetched configuration.');
    })
    .catch((err) => {
      // ...
      console.log('catch fetchAndActivate', err);
    });
}

// Initialize Analytics (Web)
const getFirebaseAnalytics = () => getAnalytics(app);

// Add these functions to your exported logic
export const logEvent = (name: string, params?: object) => {
  webLogEvent(getFirebaseAnalytics(), name, params);
};

export const setAnalyticsUserId = (id: string) => {
  webSetUserId(getFirebaseAnalytics(), id);
};

export const setUserProperties = (properties: Record<string, any>) => {
  webSetUserProperties(getFirebaseAnalytics(), properties);
}

// Only auto-run in the browser context
if (typeof window !== 'undefined') {
  fetchAndActivateRemoteConfig();
}

function getRemoteConfigByKey(key: string) {
  return getValue(getFirebaseRC(), key);
}

export function getRemoteConfigNumber(key: string) {
  return getRemoteConfigByKey(key).asNumber();
}

export function getRemoteConfigBoolean(key: string) {
  return getRemoteConfigByKey(key).asBoolean();
}

export function getRemoteConfigString(key: string) {
  return getRemoteConfigByKey(key).asString();
}

function onQuerySnapshot<T>(input: OnQuerySnapshotInput<T>): Unsubscribe {
  const {
    collection: coll,
    where: whereClauses = [],
    order: orderClauses = [],
    onNext,
    onError,
  } = input;

  const db = getFirebaseDb(); // Lazy load DB
  const filteredWhereClauses = whereClauses.filter(Boolean) as WhereClause[];
  const q = query(
    collection(db, coll),
    ...filteredWhereClauses.map(([field, op, value]) =>
      where(field, op, value),
    ),
    ...orderClauses.map(([field, dir]) => orderBy(field, dir)),
  );

  return onSnapshot(
    q,
    (snapshot: QuerySnapshot) => {
      const data = snapshot.docs.map((doc) => doc.data()) as T[];
      onNext(data);
    },
    onError,
  );
}

// 5. Auth Logic
export const loginToFirebase = async () => {
  try {
    const auth = getFirebaseAuth();
    const { fireStoreToken } = await fetchFirebaseCustomToken();
    await setData('firebaseCustomToken', fireStoreToken);
    await signInWithCustomToken(auth, fireStoreToken);
  } catch (error) {
    logger.error('Firebase Login Error:', error);
    throw error;
  }
};

export const logout: () => Promise<void> = async () => {
  try {
    await signOut(getFirebaseAuth());
  } catch (error) {
    logger.error('Firebase Logout Error:', error);
  }
};

export const firebase: Firebase = {
  getRemoteConfigString,
  getRemoteConfigNumber,
  getRemoteConfigBoolean,
  fetchAndActivateRemoteConfig,
  onQuerySnapshot,
  onMultipleQueriesSnapshot: makeOnMultipleQueriesSnapshot(onQuerySnapshot),
  loginToFirebase,
  logout,
  logEvent,
  setAnalyticsUserId,
  setUserProperties,
};